/**
 * Luciol sticky order bar (Figma node 137:168).
 *
 * Always fixed at the bottom of the PDP. Shows the order summary for the current PDP selection:
 *   Bouquet  = variant price × quantity
 *   Extras   = selected upsells (greeting card + extras) — luciol-upsells.js
 *   Delivery = rate from the delivery block — product-delivery-selector.js
 *              emits `luciol:delivery-summary`
 *   Total    = bouquet + extras + delivery
 *
 * The button submits the main product form (form="…" attribute), so the
 * same validation/upsell/Zapiet logic runs as for the main button. While a
 * delivery address is missing the button looks inactive ("Enter address to
 * continue") and scrolls to the address field instead of submitting.
 */
if (!customElements.get('luciol-order-bar')) {
  customElements.define(
    'luciol-order-bar',
    class LuciolOrderBar extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) return;

        this.priceCents = Number(this.dataset.variantPrice) || 0;
        this.available = this.dataset.variantAvailable !== 'false';
        this.currency = this.dataset.currency || 'USD';
        this.delivery = null;

        const q = (sel) => this.querySelector(sel);
        this.els = {
          qty: q('[data-lob-qty]'),
          bouquet: q('[data-lob-bouquet]'),
          extrasCell: q('[data-lob-extras-cell]'),
          extras: q('[data-lob-extras]'),
          deliveryCell: q('[data-lob-delivery-cell]'),
          deliveryLabel: q('[data-lob-delivery-label]'),
          delivery: q('[data-lob-delivery]'),
          enterAddress: q('[data-lob-enter-address]'),
          total: q('[data-lob-total]'),
          plus: q('[data-lob-plus]'),
          line: q('[data-lob-line]'),
          button: q('[data-lob-button]'),
          buttonText: q('[data-lob-button-text]'),
        };

        document.body.classList.add('lob-enabled');

        // Delivery block state (it may have initialised before us).
        const selector = this.form.querySelector('[data-product-delivery-selector]') ||
          document.querySelector('[data-product-delivery-selector]');
        this.hasDeliveryBlock = !!selector;
        if (selector?.luciolSummary) this.delivery = selector.luciolSummary;
        document.addEventListener('luciol:delivery-summary', (e) => {
          if (e.detail?.form && e.detail.form !== this.form) return;
          this.delivery = e.detail;
          this.render();
        });

        document.addEventListener('luciol:upsells-change', () => this.render());
        // Variant switch. The theme's `variant:changed` DOM event carries a
        // stale variant, so the price is read from the form's id input
        // (looked up in the embedded variants map) on every render, and we
        // only use these events as "something changed" triggers.
        try {
          this.variants = JSON.parse(this.querySelector('[data-lob-variants]')?.textContent || '{}');
        } catch (e) {
          this.variants = {};
        }
        const rerender = () => setTimeout(() => this.render(), 0);
        document.addEventListener('variant:changed', rerender);
        if (window.FoxTheme?.pubsub?.subscribe) {
          FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, rerender);
        }
        ['change', 'input'].forEach((type) =>
          document.addEventListener(type, (e) => {
            if (e.target?.name === 'quantity' && this.isOwnField(e.target)) this.render();
            else if (e.target?.closest?.('variant-selects, variant-radios, .product-form__input')) rerender();
          })
        );
        // quantity +/- buttons change the value without always firing input on the field
        document.addEventListener('click', (e) => {
          if (e.target.closest?.('quantity-input')) setTimeout(() => this.render(), 0);
        });

        this.els.button.addEventListener('click', (e) => {
          if (this.els.button.classList.contains('is-inactive')) {
            e.preventDefault();
            this.goToAddress();
          }
        });
        this.els.enterAddress?.addEventListener('click', () => this.goToAddress());
        this.els.line.addEventListener('click', (e) => {
          if (e.target.closest('[data-lob-enter-address-inline]')) this.goToAddress();
        });

        this.mirrorMainButton();
        this.observeVisibility();
        this.render();
      }

      isOwnField(el) {
        return this.form.contains(el) || el.getAttribute('form') === this.form.id;
      }

      get quantity() {
        const input = this.form.querySelector('[name="quantity"]') ||
          document.querySelector(`[name="quantity"][form="${this.form.id}"]`);
        return Math.max(1, Number(input?.value) || 1);
      }

      money(cents) {
        try {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency }).format(cents / 100);
        } catch (e) {
          return `$${(cents / 100).toFixed(2)}`;
        }
      }

      syncVariant() {
        const id = String(this.form.querySelector('[name="id"]')?.value || '');
        const v = this.variants?.[id];
        if (!v) return;
        this.priceCents = Number(v.price) || 0;
        this.available = !!v.available;
      }

      render() {
        this.syncVariant();
        const qty = this.quantity;
        const bouquetCents = this.priceCents * qty;
        const extrasCents = window.LuciolUpsells?.selectedCents?.(this.form) || 0;
        const d = this.delivery;

        let deliveryLabel = 'Delivery';
        let deliveryText = '';
        let deliveryCents = 0;
        let needsAddress = false;
        if (this.hasDeliveryBlock) {
          if (d?.status === 'pickup') {
            deliveryLabel = 'Pickup';
            deliveryText = 'Free';
          } else if (d?.status === 'priced') {
            deliveryCents = d.rateCents || 0;
            deliveryText = deliveryCents === 0 ? 'Free' : this.money(deliveryCents);
          } else {
            needsAddress = true;
          }
        }

        // Desktop cells
        this.els.qty.textContent = qty;
        this.els.bouquet.textContent = this.money(bouquetCents);
        this.els.extrasCell.hidden = extrasCents <= 0;
        this.els.extras.textContent = this.money(extrasCents);
        this.els.deliveryCell.hidden = !this.hasDeliveryBlock;
        this.els.deliveryLabel.textContent = deliveryLabel;
        this.els.delivery.hidden = needsAddress;
        this.els.delivery.textContent = deliveryText;
        this.els.enterAddress.hidden = !needsAddress;

        // Total
        this.els.total.textContent = this.money(bouquetCents + extrasCents + deliveryCents);
        this.els.plus.hidden = !needsAddress;

        // Mobile breakdown line
        const parts = [`Bouquet <b>${this.money(bouquetCents)}</b>`];
        if (extrasCents > 0) parts.push(`Extras <b>${this.money(extrasCents)}</b>`);
        if (this.hasDeliveryBlock) {
          parts.push(
            needsAddress
              ? `Delivery <button type="button" class="lob__link" data-lob-enter-address-inline>Enter address</button>`
              : `${deliveryLabel} <b>${deliveryText}</b>`
          );
        }
        this.els.line.innerHTML = parts.join('<span class="lob__sep" aria-hidden="true">&middot;</span>');

        // Button
        const btn = this.els.button;
        btn.classList.toggle('is-inactive', this.available && needsAddress);
        btn.disabled = !this.available;
        this.els.buttonText.textContent = !this.available
          ? 'Sold out'
          : needsAddress
          ? 'Enter address to continue'
          : 'Add To Cart';

        this.updateHeight();
      }

      goToAddress() {
        const selector = document.querySelector('[data-product-delivery-selector]');
        const input = selector?.querySelector('[data-delivery-address]');
        const target = input?.closest('.product-delivery-selector__field') || input || selector;
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (input) setTimeout(() => input.focus({ preventScroll: true }), 450);
      }

      // Mirror the main button's loading state while the form submits.
      mirrorMainButton() {
        const main = this.form.querySelector('[type="submit"][name="add"]');
        if (!main) return;
        const sync = () => {
          const busy = main.getAttribute('aria-disabled') === 'true' || main.classList.contains('btn--loading');
          this.els.button.classList.toggle('is-loading', busy);
        };
        new MutationObserver(sync).observe(main, { attributes: true, attributeFilter: ['class', 'aria-disabled'] });
        sync();
      }

      // Always visible (fixed to the bottom of the viewport).
      observeVisibility() {
        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('inert');
        this.updateHeight();
        window.addEventListener('resize', () => this.updateHeight());
        if ('ResizeObserver' in window) new ResizeObserver(() => this.updateHeight()).observe(this);
      }

      updateHeight() {
        const h = this.classList.contains('is-visible') ? this.offsetHeight : 0;
        document.documentElement.style.setProperty('--sticky-atc-bar-height', `${h}px`);
      }
    }
  );
}
